import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { companyInfo } from '../data/mock';
import '../styles/pages.css';
import usePageMeta from '../hooks/usePageMeta';
import StoreButtons from '../components/home/StoreButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Validadores
const validateEmail = (value) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!value) return 'El correo electrónico es requerido';
  if (!emailRegex.test(value)) return 'Ingrese un correo electrónico válido';
  return '';
};

const validateName = (value) => {
  if (!value) return 'El nombre es requerido';
  if (value.length < 3) return 'El nombre debe tener al menos 3 caracteres';
  if (value.length > 100) return 'El nombre es demasiado largo';
  return '';
};

const validatePhone = (value) => {
  if (!value) return '';
  const cleanPhone = value.replace(/\D/g, '');
  if (cleanPhone.length > 0 && cleanPhone.length !== 10) {
    return 'El teléfono debe tener 10 dígitos';
  }
  return '';
};

const validateMessage = (value) => {
  if (!value) return 'El mensaje es requerido';
  if (value.length < 20) return 'El mensaje debe tener al menos 20 caracteres';
  if (value.length > 2000) return 'El mensaje es demasiado largo (máx. 2000 caracteres)';
  return '';
};

const validateCompany = (value) => {
  if (value && value.length > 100) return 'El nombre de la empresa es demasiado largo';
  return '';
};

// Formateador de teléfono: agrupa en 3-3-4, como se escriben los números
// mexicanos de 10 dígitos (551 234 5678). Antes agrupaba 2-4-4 con
// paréntesis y guion, que no corresponde al formato local.
const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

// Title Case
const toTitleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// Limpiar espacios
const cleanSpaces = (str) => {
  if (!str) return '';
  return str.replace(/\s+/g, ' ').trim();
};

const Contact = () => {
  usePageMeta({
    title: 'Contacto',
    description: 'Solicite una evaluación inicial sin compromiso. Nuestro equipo analizará sus necesidades y le presentará una propuesta personalizada.',
    path: '/contact',
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    eventType: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Manejar cambios
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let newValue = value;
    
    // Solo formatear teléfono
    if (name === 'phone') {
      newValue = formatPhone(value);
    }
    
    setFormData(prevData => ({
      ...prevData,
      [name]: newValue
    }));
    
    // Validar si ya fue tocado
    if (touched[name]) {
      let error = '';
      switch (name) {
        case 'name': error = validateName(newValue); break;
        case 'email': error = validateEmail(newValue); break;
        case 'phone': error = validatePhone(newValue); break;
        case 'message': error = validateMessage(newValue); break;
        case 'company': error = validateCompany(newValue); break;
        default: break;
      }
      setErrors(prevErrors => ({ ...prevErrors, [name]: error }));
    }
  };

  // Manejar blur
  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marcar como tocado
    setTouched(prevTouched => ({ ...prevTouched, [name]: true }));
    
    // Normalizar al salir
    let normalizedValue = value;
    if (name === 'name' || name === 'company') {
      normalizedValue = toTitleCase(cleanSpaces(value));
    } else if (name === 'email') {
      normalizedValue = value.toLowerCase().trim();
    } else if (name === 'message') {
      normalizedValue = cleanSpaces(value);
    }
    
    if (normalizedValue !== value) {
      setFormData(prevData => ({ ...prevData, [name]: normalizedValue }));
    }
    
    // Validar
    let error = '';
    switch (name) {
      case 'name': error = validateName(normalizedValue); break;
      case 'email': error = validateEmail(normalizedValue); break;
      case 'phone': error = validatePhone(normalizedValue); break;
      case 'message': error = validateMessage(normalizedValue); break;
      case 'company': error = validateCompany(normalizedValue); break;
      default: break;
    }
    setErrors(prevErrors => ({ ...prevErrors, [name]: error }));
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marcar todos como tocados
    const allTouched = { name: true, email: true, company: true, phone: true, eventType: true, message: true };
    setTouched(allTouched);
    
    // Validar todo
    const newErrors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
      message: validateMessage(formData.message),
      company: validateCompany(formData.company)
    };
    
    // Filtrar errores vacíos
    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, v]) => v !== '')
    );
    
    setErrors(filteredErrors);
    
    if (Object.keys(filteredErrors).length > 0) {
      const firstError = Object.keys(filteredErrors)[0];
      document.getElementById(firstError)?.focus();
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Preparar datos para enviar (limpiar teléfono)
      const dataToSend = {
        name: formData.name,
        email: formData.email,
        company: formData.company || '',
        phone: formData.phone.replace(/\D/g, ''), // Solo dígitos
        eventType: formData.eventType || '',
        message: formData.message
      };
      
      // Enviar al backend
      await axios.post(`${API}/contact`, dataToSend);
      
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      setSubmitError('Hubo un error al enviar el formulario. Por favor intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInputClass = (name) => {
    let className = 'form-group';
    if (errors[name] && touched[name]) className += ' has-error';
    else if (touched[name] && !errors[name] && formData[name]) className += ' is-valid';
    return className;
  };

  return (
    <Layout>
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Contacto</h1>
          <p>Estamos listos para atender sus necesidades de seguridad</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info-section">
              <h2>Información de Contacto</h2>
              <p>
                Contáctenos para una evaluación inicial de su evento. 
                Nuestro equipo responderá a la brevedad.
              </p>
              
              <div className="contact-details">
                <a href={`mailto:${companyInfo.email}`} className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Mail size={20} />
                  </div>
                  <div>
                    <span className="contact-detail-label">Correo electrónico</span>
                    <span className="contact-detail-value">{companyInfo.email}</span>
                  </div>
                </a>
                
                <a href={`tel:${companyInfo.phone}`} className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Phone size={20} />
                  </div>
                  <div>
                    <span className="contact-detail-label">Teléfono</span>
                    <span className="contact-detail-value">{companyInfo.phone}</span>
                  </div>
                </a>
                
                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <span className="contact-detail-label">Ubicación</span>
                    <span className="contact-detail-value">{companyInfo.address}</span>
                  </div>
                </div>
              </div>

              <div className="contact-note">
                <p>
                  <strong>Horario de atención:</strong><br />
                  Lunes a Viernes: 9:00 - 18:00 hrs<br />
                  Atención de emergencias: 24/7
                </p>
              </div>

              <div className="contact-app">
                <h3 className="contact-app-title">Descargue la aplicación</h3>
                <p className="contact-app-text">
                  Levante la evaluación en el recinto, con evidencia y sin
                  depender de la señal.
                </p>
                <StoreButtons className="contact-app-buttons" />
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-section">
              {isSubmitted ? (
                <div className="form-success">
                  <div className="success-icon">
                    <CheckCircle size={48} />
                  </div>
                  <h3>Mensaje enviado</h3>
                  <p>
                    Gracias por contactarnos. Nuestro equipo revisará su solicitud 
                    y se pondrá en contacto con usted a la brevedad.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form" noValidate>
                  <h3>Solicitar Información</h3>
                  
                  <div className="form-row">
                    {/* Nombre */}
                    <div className={getInputClass('name')}>
                      <label htmlFor="name">
                        Nombre completo <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Ej: Juan Pérez García"
                          maxLength={100}
                        />
                        {touched.name && !errors.name && formData.name && (
                          <CheckCircle className="input-icon valid" size={18} />
                        )}
                        {errors.name && touched.name && (
                          <AlertCircle className="input-icon invalid" size={18} />
                        )}
                      </div>
                      {errors.name && touched.name && (
                        <span className="error-message">{errors.name}</span>
                      )}
                    </div>

                    {/* Email */}
                    <div className={getInputClass('email')}>
                      <label htmlFor="email">
                        Correo electrónico <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="correo@ejemplo.com"
                          maxLength={100}
                        />
                        {touched.email && !errors.email && formData.email && (
                          <CheckCircle className="input-icon valid" size={18} />
                        )}
                        {errors.email && touched.email && (
                          <AlertCircle className="input-icon invalid" size={18} />
                        )}
                      </div>
                      {errors.email && touched.email && (
                        <span className="error-message">{errors.email}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    {/* Empresa */}
                    <div className={getInputClass('company')}>
                      <label htmlFor="company">Empresa / Organización</label>
                      <div className="input-wrapper">
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Nombre de su empresa"
                          maxLength={100}
                        />
                        {touched.company && !errors.company && formData.company && (
                          <CheckCircle className="input-icon valid" size={18} />
                        )}
                        {errors.company && touched.company && (
                          <AlertCircle className="input-icon invalid" size={18} />
                        )}
                      </div>
                      {errors.company && touched.company && (
                        <span className="error-message">{errors.company}</span>
                      )}
                    </div>

                    {/* Teléfono */}
                    <div className={getInputClass('phone')}>
                      <label htmlFor="phone">Teléfono (10 dígitos)</label>
                      <div className="input-wrapper">
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="551 234 5678"
                          maxLength={12}
                        />
                        {touched.phone && !errors.phone && formData.phone && (
                          <CheckCircle className="input-icon valid" size={18} />
                        )}
                        {errors.phone && touched.phone && (
                          <AlertCircle className="input-icon invalid" size={18} />
                        )}
                      </div>
                      {errors.phone && touched.phone && (
                        <span className="error-message">{errors.phone}</span>
                      )}
                    </div>
                  </div>

                  {/* Tipo de evento */}
                  <div className="form-group">
                    <label htmlFor="eventType">Tipo de evento</label>
                    <select
                      id="eventType"
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleChange}
                    >
                      <option value="">Seleccione una opción</option>
                      <option value="deportivo">Evento deportivo</option>
                      <option value="concierto">Concierto / Espectáculo</option>
                      <option value="corporativo">Evento corporativo</option>
                      <option value="convencion">Convención / Exposición</option>
                      <option value="academico">Evento académico</option>
                      <option value="masivo">Evento masivo</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>

                  {/* Mensaje */}
                  <div className={getInputClass('message')}>
                    <label htmlFor="message">
                      Mensaje <span className="required">*</span>
                      <span className="char-count">{formData.message.length}/2000</span>
                    </label>
                    <div className="input-wrapper">
                      <textarea
                        id="message"
                        name="message"
                        rows="5"
                        value={formData.message}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Cuéntenos sobre su evento y sus necesidades de seguridad (mínimo 20 caracteres)..."
                        maxLength={2000}
                      />
                    </div>
                    {errors.message && touched.message && (
                      <span className="error-message">{errors.message}</span>
                    )}
                  </div>

                  {submitError && (
                    <div className="submit-error">
                      <AlertCircle size={18} />
                      <span>{submitError}</span>
                    </div>
                  )}

                  <button 
                    type="submit" 
                    className="btn btn-primary btn-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar mensaje
                        <Send size={18} />
                      </>
                    )}
                  </button>

                  <p className="form-disclaimer">
                    Al enviar este formulario, acepta nuestro <a href="/legal">aviso de privacidad</a>.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
